import { useState } from 'react';
import { useDeviceMutations, useDevices, type Device, type DeviceList } from '@/api/endpointsInstall';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DeviceRows } from '@/features/devices/DeviceRows';
import { RegisterDeviceSheet } from '@/features/devices/RegisterDeviceSheet';
import { anyWaiting, classCounts } from '@/features/devices/devicesModel';
import { showMutationError } from '@/lib/mutationError';
import { useCan } from '@/lib/permissions';
import { useNow } from '@/lib/useNow';

/**
 * iOS devices of the organization (SPEC decision 28): the iPhones and iPads internal iOS builds install on. Org admins
 * register one with a QR code (an over-the-air profile sends its UDID to Hangar, which registers it with Apple through
 * the team's App Store Connect key); members see the list. Apple's limits are shown next to the counts.
 */
export function DevicesPage() {
  const canRead = useCan('credentials:read');
  const canWrite = useCan('credentials:write');
  const now = useNow(5_000);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [session, setSession] = useState(0);
  const devices = useDevices(canRead.allowed, sheetOpen, (list) => anyWaiting(list?.data, Date.now()));
  const { register, remove } = useDeviceMutations();
  const [removing, setRemoving] = useState<Device | null>(null);
  const data = devices.data;

  const openSheet = (open: boolean) => {
    if (open) setSession((n) => n + 1);
    else register.reset();
    setSheetOpen(open);
  };
  const confirmRemove = async () => {
    if (!removing) return;
    try { await remove.mutateAsync(removing.id); setRemoving(null); } catch (e) { showMutationError(e, 'Could not remove the device'); }
  };

  return (
    <div className="flex flex-col gap-5">
      <header className="enter flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 max-w-2xl">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">iOS devices</h1>
          <p className="mt-1 text-sm text-muted-foreground">The iPhones and iPads your team installs internal iOS builds on. A build installs only on devices registered before it was built.</p>
        </div>
        <GatedButton can={canWrite} onClick={() => openSheet(true)} data-testid="devices-register">
          <Icon name="add" size={18} />
          Register a device
        </GatedButton>
      </header>
      {!canRead.allowed ? (
        <EmptyState icon="lock" title="Visible to your organization's members" description="Ask an org admin to register your device." testId="devices-no-access" />
      ) : (
        <DevicesBody query={devices} data={data} now={now} onRegister={canWrite.allowed ? () => openSheet(true) : null} onRemove={canWrite.allowed ? setRemoving : null} />
      )}
      <RegisterDeviceSheet key={session} open={sheetOpen} onOpenChange={openSheet} onCreate={(name) => register.mutateAsync(name || undefined)} creating={register.isPending} error={register.error} devices={data?.data} />
      <ConfirmArchiveDialog
        open={removing !== null}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
        title={`Remove ${removing?.name ?? 'the device'}?`}
        description="Hangar stops including it in new iOS builds and disables it at Apple. Apple keeps counting it against this year's limit."
        impact={[{ text: <>Builds already made keep working on it</> }]}
        confirmLabel="Remove"
        busy={remove.isPending}
        onConfirm={() => void confirmRemove()}
        testId="device-remove-dialog"
      />
    </div>
  );
}

interface BodyProps {
  query: ReturnType<typeof useDevices>;
  data: DeviceList | undefined;
  now: number;
  onRegister: (() => void) | null;
  onRemove: ((d: Device) => void) | null;
}

function DevicesBody({ query, data, now, onRegister, onRemove }: BodyProps) {
  if (query.isLoading && !data) return <BodySkeleton />;
  if (query.isError && !data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!data) return null;
  return (
    <>
      <AppleLimits data={data} />
      <Card className="enter overflow-hidden p-0">
        {data.data.length === 0 ? (
          <EmptyState icon="phone_iphone" title="No devices yet" description="Register the iPhones and iPads your team tests on: scan a code with the device and it sends its identifier to Hangar." testId="devices-empty"
            action={onRegister ? <GatedButton onClick={onRegister} variant="outline">Register a device</GatedButton> : undefined} />
        ) : <DeviceRows devices={data.data} now={now} onRemove={onRemove} />}
      </Card>
    </>
  );
}

function AppleLimits({ data }: { data: DeviceList }) {
  return (
    <section className="enter grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center" aria-label="Apple's limits" data-testid="devices-limits">
      <div className="flex flex-wrap gap-2">
        {classCounts(data).map((c) => (
          <div key={c.deviceClass} className="rounded-lg border bg-card px-3 py-2 shadow-card">
            <div className="text-xs text-subtle">{c.label}</div>
            <div className="tabular font-mono text-lg font-semibold text-foreground">{c.count}<span className="text-sm font-normal text-subtle"> of {c.limit}</span></div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Apple allows {data.limits.devicesPerClassPerYear} devices of each kind per membership year; a removed device still counts until the year renews. A new Apple Developer membership can take up to {data.limits.newMembershipDelayHours} hours before its devices can be used.</p>
    </section>
  );
}

function BodySkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="flex gap-2"><Skeleton className="h-14 w-28 rounded-lg" /><Skeleton className="h-14 w-28 rounded-lg" /></div>
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  );
}
